import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Badge, Button } from '@/components/atoms';
import { ApiError } from '@/services/api';
import { categoriesApi } from '@/services/articles';
import { destinationsApi } from '@/services/config';
import { detalheLegivel } from '@/services/erros';
import { factoriesApi } from '@/services/factories';
import { metricsApi } from '@/services/metrics';
import { sanitizarHtml } from '@/utils/sanitize';
import {
  editoriasApi,
  type Editoria,
  type EditoriaPatch,
  type Estado,
  type FeedKind,
  type IngestResult,
  type Mode,
  type ProcMode,
  type RunResult,
} from '@/services/editorias';

import styles from './Editorias.module.css';

const FEED_LABEL: Record<FeedKind, string> = {
  google_news: 'Google News',
  rss: 'RSS',
  api: 'API',
  serp_trends: 'SERP · Trends',
  serp_events: 'SERP · Eventos (geo)',
  serp_finance: 'SERP · Finance',
  mos: 'MOS · NRCS',
  fabrica: 'Fábrica · dado',
};
const PROC_LABEL: Record<ProcMode, string> = {
  '1x1': '1×1 · reescreve 1 fonte',
  multi_fonte: 'Multi-fonte · agrupa + remonta 5W1H',
  enriquecer: 'Enriquecer · matéria pronta',
  dados: 'Dados → matéria',
  template: 'Template determinístico (sem IA)',
};
const PROC_SHORT: Record<ProcMode, string> = {
  '1x1': '1×1',
  multi_fonte: 'multi-fonte',
  enriquecer: 'enriquecer',
  dados: 'dados',
  template: 'template',
};
const ESTADO_META: Record<Estado, { label: string; tone: 'ok' | 'warn' | 'info' }> = {
  no_ar: { label: 'no ar', tone: 'ok' },
  migrar: { label: 'migrar do N8N', tone: 'warn' },
  nova: { label: 'nova', tone: 'info' },
};
const MODES: Mode[] = ['off', 'semi', 'auto'];
// Modelos disponíveis hoje (só OpenAI ligado; a lista completa do BYOK vem no E4).
const LLM_MODELS = ['gpt-4o', 'gpt-4o-mini', 'gpt-4.1-mini', 'gpt-5.1'];

// Ao trocar a fonte, re-arma as variáveis para o formato daquela fonte.
const FEED_TEMPLATES: Record<FeedKind, { fonte: string; query: string; url: string; intervalo: string }> = {
  google_news: { fonte: "Google News (busca)", query: "", url: "", intervalo: "8h" },
  rss: { fonte: "Feed RSS do veículo", query: "", url: "https://", intervalo: "1h" },
  api: { fonte: "API (driver em breve)", query: "", url: "", intervalo: "15min" },
  serp_trends: { fonte: "SERP · Trending now (pt-BR)", query: "", url: "", intervalo: "1h" },
  serp_events: { fonte: "SERP · Eventos Google (geo)", query: "eventos", url: "", intervalo: "10-12h úteis" },
  serp_finance: { fonte: "SERP · Google Finance", query: "", url: "", intervalo: "15-17h úteis" },
  // "tempo real" e "janela BRT" eram RÓTULOS, não gramática: nenhum parser do
  // agendador os lê. Enquanto valiam 200 no PUT, a editoria ficava com um texto
  // na tela e uma janela invisível no worker (o default de 8h / o default da
  // fábrica). Com a borda validando (F-440) eles virariam 422 na simples troca
  // de fonte, então o template passa a escrever cadência/janela de verdade.
  mos: { fonte: "MOS · rundown do NRCS (push)", query: "", url: "", intervalo: "15min" },
  fabrica: { fonte: "Platform Core (dado estruturado)", query: "", url: "", intervalo: "6-8h" },
};

// Janela default por fábrica — o MESMO mapa que o backend usa como rede de
// segurança (`services/factories_publisher._JANELA_DEFAULT`). Sem isto, trocar
// a fonte de "Loterias" para fábrica gravaria a janela da manhã por cima do
// sorteio das 21h.
const JANELA_FABRICA: Record<string, string> = {
  clima: '6-8h',
  mercado: '18-19h úteis',
  loterias: '21-23h',
};

// Rótulos legíveis dos jogos (a lista válida vem da API; isto é só o display).
const GAME_LABEL: Record<string, string> = {
  megasena: 'Mega-Sena',
  quina: 'Quina',
  lotofacil: 'Lotofácil',
  lotomania: 'Lotomania',
  timemania: 'Timemania',
  duplasena: 'Dupla Sena',
  diadesorte: 'Dia de Sorte',
  supersete: 'Super Sete',
  maismilionaria: '+Milionária',
  federal: 'Federal',
  loteca: 'Loteca',
};
const DEFAULT_JOGOS = ['megasena', 'quina', 'lotofacil'];
const CAPITAIS_CHIPS = [
  { value: 'sao-paulo', label: 'São Paulo' },
  { value: 'rio-de-janeiro', label: 'Rio de Janeiro' },
  { value: 'brasilia', label: 'Brasília' },
  { value: 'belo-horizonte', label: 'Belo Horizonte' },
  { value: 'salvador', label: 'Salvador' },
  { value: 'curitiba', label: 'Curitiba' },
  { value: 'porto-alegre', label: 'Porto Alegre' },
  { value: 'recife', label: 'Recife' },
  { value: 'fortaleza', label: 'Fortaleza' },
  { value: 'manaus', label: 'Manaus' },
  { value: 'belem', label: 'Belém' },
  { value: 'joao-pessoa', label: 'João Pessoa' },
];

// Seletor de tags por clique — elimina o typo silencioso do texto-com-vírgula.
function ChipToggle({
  options,
  selected,
  onChange,
}: {
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <div className={styles.chips} role="group">
      {options.map((o) => {
        const on = selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            className={`${styles.tagChip} ${on ? styles.tagChipOn : ''}`}
            aria-pressed={on}
            onClick={() => toggle(o.value)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Monta a URL do Google News a partir do termo — o usuário nunca toca nos params.
function gnUrl(feed: { query?: string; url?: string }): string {
  const q = (feed.query ?? '').trim();
  if (q)
    return `https://news.google.com/rss/search?q=${encodeURIComponent(q)}&hl=pt-BR&gl=BR&ceid=BR:pt-419`;
  return feed.url || '(defina um termo de busca)';
}

export function Editorias() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>('cultura');
  const [ingest, setIngest] = useState<IngestResult | null>(null);
  const [draft, setDraft] = useState<RunResult | null>(null);

  const ingestMut = useMutation({
    mutationFn: (slug: string) => editoriasApi.ingest(slug),
    onSuccess: (r) => setIngest(r),
  });
  const runMut = useMutation({
    mutationFn: (slug: string) => editoriasApi.run(slug),
    onSuccess: (r) => setDraft(r),
  });
  const [pubErr, setPubErr] = useState<string | null>(null);
  const publishMut = useMutation({
    mutationFn: (v: { slug: string; live: boolean }) =>
      v.live ? editoriasApi.publishLive(v.slug) : editoriasApi.publish(v.slug),
    onSuccess: (r) => {
      setDraft(r);
      setPubErr(null);
    },
    onError: (e: unknown) => {
      const detail =
        e instanceof ApiError && e.body && typeof e.body === 'object' && 'detail' in e.body
          ? String((e.body as { detail: unknown }).detail)
          : 'Falha ao salvar no WordPress.';
      setPubErr(detail);
    },
  });

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['editorias'],
    queryFn: editoriasApi.list,
  });

  const mut = useMutation({
    mutationFn: (v: { slug: string; patch: EditoriaPatch }) => editoriasApi.patch(v.slug, v.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['editorias'] }),
  });
  const patch = (slug: string, p: EditoriaPatch) => mut.mutate({ slug, patch: p });

  const resumo = useMemo(() => {
    const c = (e: Estado) => list.filter((x) => x.estado === e).length;
    return { total: list.length, noAr: c('no_ar'), migrar: c('migrar'), nova: c('nova') };
  }, [list]);

  const aberta = list.find((x) => x.slug === openId) ?? null;

  // Edição do bloco Feed: rascunho local (snappy) + persiste no blur / na troca de fonte.
  const [feedDraft, setFeedDraft] = useState<{ slug: string; feed: Editoria['feed'] } | null>(null);
  const feedOf = (e: Editoria) =>
    feedDraft && feedDraft.slug === e.slug ? feedDraft.feed : e.feed;
  const editFeed = (p: Partial<Editoria['feed']>) => {
    if (aberta) setFeedDraft({ slug: aberta.slug, feed: { ...feedOf(aberta), ...p } });
  };
  // A recusa da janela (422) tem de aparecer AO LADO DO CAMPO. O alarme global
  // ignora 422 de propósito (`services/api.ts`), então sem este estado a
  // validação de borda seria invisível: a pessoa saía do campo, nada mudava na
  // tela e o valor dela não tinha sido gravado.
  const [janelaErro, setJanelaErro] = useState<string | null>(null);
  const persistFeed = async (feed: Editoria['feed']) => {
    if (!aberta) return;
    setJanelaErro(null);
    try {
      await mut.mutateAsync({ slug: aberta.slug, patch: { feed } });
    } catch (e: unknown) {
      // Só o 422 é desta caixa (janela ilegível); o resto já vira alarme global.
      // O rascunho local NÃO é desfeito: o texto digitado continua no campo
      // para a pessoa corrigir em vez de ter de redigitar.
      setJanelaErro(e instanceof ApiError && e.status === 422 ? detalheLegivel(e) : null);
    }
  };
  const persistFeedNow = () => {
    if (aberta) void persistFeed(feedOf(aberta));
  };
  const changeFonte = (kind: FeedKind) => {
    if (!aberta) return;
    const tpl = FEED_TEMPLATES[kind];
    const atual = feedOf(aberta);
    // Fábrica tem janela POR fábrica — o template genérico apagaria a das 21h
    // das Loterias.
    const intervalo =
      kind === 'fabrica' ? (JANELA_FABRICA[atual.fabrica ?? ''] ?? tpl.intervalo) : tpl.intervalo;
    // Re-arma as variáveis para o formato da nova fonte.
    const next = {
      ...atual, kind,
      fonte: tpl.fonte, query: tpl.query, url: tpl.url, intervalo,
    };
    setFeedDraft({ slug: aberta.slug, feed: next });
    void persistFeed(next);
  };

  // Fábrica: dado estruturado → matéria por template (sem IA). Tem UX própria
  // (preview da capa + rodar-agora), então o rodapé de RSS não se aplica.
  const isFabrica = aberta ? feedOf(aberta).kind === 'fabrica' : false;
  // Alarmes das esteiras (janela perdida, fábrica calada) — a lição de
  // 31/07-01/08: três esteiras falharam em silêncio; agora falha grita AQUI,
  // na tela de quem opera as editorias.
  const esteiras = useQuery({
    queryKey: ['esteiras-alarmes'],
    queryFn: () => metricsApi.esteiras(),
    refetchInterval: 5 * 60 * 1000,
  });
  const alarmesEsteira = esteiras.data?.alarmes ?? [];

  const factoriesStatus = useQuery({
    queryKey: ['factories', 'status'],
    queryFn: factoriesApi.getStatus,
    enabled: isFabrica,
  });
  const preview = useQuery({
    queryKey: ['factory-preview', aberta?.slug, JSON.stringify(aberta?.pub.sites)],
    queryFn: () => factoriesApi.previewCover(aberta?.slug ?? ''),
    enabled: isFabrica && !!aberta,
  });
  const ultimas = useQuery({
    queryKey: ['editoria-ultimas', aberta?.slug],
    queryFn: () => editoriasApi.ultimas(aberta?.slug ?? ''),
    enabled: !!aberta,
  });
  const [runMsg, setRunMsg] = useState<string | null>(null);
  const runFabrica = useMutation({
    mutationFn: (args: { slug: string; client?: string }) => {
      return factoriesApi.runEditoria(args.slug, args.client);
    },
    onSuccess: (r) => {
      const runs = r.runs ?? [];
      const publicadas = runs.reduce(
        (n, x) => n + (x.loterias?.length ?? 0) + (x.mercado?.length ?? 0) + (x.clima?.length ?? 0),
        0,
      );
      setRunMsg(
        r.skipped
          ? r.skipped
          : publicadas > 0
            ? `${publicadas} matéria(s) publicada(s).`
            : 'Nada novo para publicar agora (já publicado hoje/neste concurso).',
      );
      ultimas.refetch();
    },
    onError: (e) => {
      const detail =
        e instanceof ApiError && e.body && typeof e.body === 'object' && 'detail' in e.body
          ? String((e.body as { detail: unknown }).detail)
          : e instanceof Error
            ? e.message
            : 'Falha ao rodar — veja os logs do backend.';
      setRunMsg(detail);
    },
  });

  // Selects de config (E2b): sites cadastrados + categorias do 1º site.
  const sitesQuery = useQuery({ queryKey: ['wp-sites'], queryFn: () => destinationsApi.list() });
  const firstSite = aberta?.pub.sites[0];
  const catsQuery = useQuery({
    queryKey: ['wp-cats', firstSite],
    queryFn: () => categoriesApi.list(firstSite),
    enabled: !!firstSite,
  });
  // Opções de site = SÓ os cadastrados em Config → Sites. Um slug referenciado
  // por editoria mas não cadastrado nesta instância (ex.: seed vindo de outra)
  // vira AVISO de órfão — nunca opção marcável.
  const registeredSlugs = new Set((sitesQuery.data ?? []).map((s) => s.slug));
  const siteOptions = [...registeredSlugs].sort().map((slug) => {
    const reg = (sitesQuery.data ?? []).find((s) => s.slug === slug);
    return { slug, name: reg?.name || slug };
  });
  const orphanSites = aberta ? aberta.pub.sites.filter((s) => !registeredSlugs.has(s)) : [];

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Editorias</h1>
          <p className={styles.subtitle}>
            O motor automatizador. Cada editoria é um pipeline serial de 3 blocos —{' '}
            <strong>Feed</strong> → <strong>Processamento</strong> → <strong>Publicação</strong> —
            onde só os dados mudam. Linha nova, paralela ao N8N: nada em produção sai até o swap.
          </p>
        </div>
        <Button disabled title="Em breve — as editorias nascem do seed; criação manual virá depois">
          + Nova editoria
        </Button>
      </header>

      <div className={styles.resumo}>
        <span className={styles.chip}>
          <b>{resumo.total}</b> editorias
        </span>
        <span className={`${styles.chip} ${styles.chipOk}`}>
          <b>{resumo.noAr}</b> no ar
        </span>
        <span className={`${styles.chip} ${styles.chipWarn}`}>
          <b>{resumo.migrar}</b> a migrar do N8N
        </span>
        <span className={`${styles.chip} ${styles.chipInfo}`}>
          <b>{resumo.nova}</b> novas
        </span>
      </div>

      {sitesQuery.isSuccess && (sitesQuery.data?.length ?? 0) === 0 && (
        <div className={styles.emptyBanner}>
          <span>
            Nenhum site WordPress cadastrado — as editorias precisam de um destino para publicar.
          </span>
          <Link to="/config/sites" className={styles.emptyCta}>
            Cadastrar site →
          </Link>
        </div>
      )}

      {alarmesEsteira.length > 0 && (
        <div className={styles.alarmesBanner} role="alert">
          <div className={styles.alarmesHead}>
            <span className={styles.alarmesFlag}>◈</span>
            <strong>{alarmesEsteira.length}</strong>&nbsp;
            {alarmesEsteira.length === 1
              ? 'esteira automática precisa de atenção'
              : 'esteiras automáticas precisam de atenção'}
          </div>
          <ul className={styles.alarmesLista}>
            {alarmesEsteira.map((a, i) => (
              <li
                key={`${a.job}-${i}`}
                className={a.gravidade === 'alta' ? styles.alarmeAlto : styles.alarmeMedio}
              >
                {a.detalhe}
              </li>
            ))}
          </ul>
        </div>
      )}

      {isLoading && <p className={styles.proto}>Carregando editorias…</p>}

      <div className={styles.grid}>
        {list.map((e) => (
          <button
            key={e.slug}
            type="button"
            className={`${styles.card} ${openId === e.slug ? styles.cardOpen : ''}`}
            onClick={() => {
              setJanelaErro(null);
              setOpenId(openId === e.slug ? null : e.slug);
            }}
          >
            <div className={styles.cardHead}>
              <span className={styles.cardName}>{e.nome}</span>
              <Badge tone={ESTADO_META[e.estado].tone} size="xs">
                {ESTADO_META[e.estado].label}
              </Badge>
            </div>
            <div className={styles.pipe}>
              <span className={styles.pipeBlk}>{FEED_LABEL[e.feed.kind]}</span>
              <span className={styles.pipeArrow}>›</span>
              <span className={styles.pipeBlk}>{PROC_SHORT[e.proc.mode]}</span>
              <span className={styles.pipeArrow}>›</span>
              <span className={styles.pipeBlk}>
                {e.pub.status === 'publish' ? 'publica' : 'rascunho'}
              </span>
            </div>
            <div className={styles.cardFoot}>
              <span className={`${styles.modo} ${styles[`modo-${e.modo}`]}`}>{e.modo}</span>
              <span className={styles.cardMeta}>
                {e.pub.porHora}/h · {e.feed.intervalo}
              </span>
            </div>
          </button>
        ))}
      </div>

      {aberta && (
        <div className={styles.detalhe}>
          <div className={styles.detHead}>
            <div>
              <h2 className={styles.detTitle}>{aberta.nome}</h2>
              <span className={styles.detSub}>
                Pipeline · {FEED_LABEL[feedOf(aberta).kind]} → {PROC_LABEL[aberta.proc.mode]} →{' '}
                {aberta.pub.sites.join(', ')}
              </span>
            </div>
            <div className={styles.modeSel}>
              {MODES.map((m) => (
                <button
                  key={m}
                  type="button"
                  title={
                    m === 'auto' ? 'Roda sozinha no intervalo do feed (worker horário)' : undefined
                  }
                  className={`${styles.modeBtn} ${aberta.modo === m ? styles.modeBtnOn : ''}`}
                  onClick={() => patch(aberta.slug, { modo: m })}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {aberta.flags?.map((f) => (
            <p key={f} className={styles.flag}>
              {f}
            </p>
          ))}

          <div className={styles.blocos}>
            {/* BLOCO 1 — FEED */}
            <div className={styles.bloco}>
              <div className={styles.blocoHead}>
                <span className={styles.blocoNum}>1</span> Feed
              </div>
              <Field label="Fonte">
                <select
                  className={styles.input}
                  value={feedOf(aberta).kind}
                  onChange={(ev) => changeFonte(ev.target.value as FeedKind)}
                >
                  {(Object.keys(FEED_LABEL) as FeedKind[]).map((k) => (
                    <option key={k} value={k}>
                      {FEED_LABEL[k]}
                    </option>
                  ))}
                </select>
              </Field>
              {feedOf(aberta).kind === 'google_news' && (
                <>
                  <Field label="Termo de busca">
                    <input
                      className={styles.input}
                      value={feedOf(aberta).query ?? ''}
                      placeholder="ex.: cultura, eleições, São Paulo"
                      onChange={(e) => editFeed({ query: e.target.value })}
                      onBlur={persistFeedNow}
                    />
                  </Field>
                  <Field label="URL gerada (travada)">
                    <div className={styles.ro} title={gnUrl(feedOf(aberta))}>
                      {gnUrl(feedOf(aberta))}
                    </div>
                  </Field>
                </>
              )}
              {feedOf(aberta).kind === 'serp_events' && (
                <>
                  <Field label="Termo (eventos)">
                    <input
                      className={styles.input}
                      value={feedOf(aberta).query ?? ''}
                      placeholder='ex.: "eventos", "shows", "teatro"'
                      onChange={(e) => editFeed({ query: e.target.value })}
                      onBlur={persistFeedNow}
                    />
                  </Field>
                  <Field label="Cidade (geolocalização)">
                    <input
                      className={styles.input}
                      value={feedOf(aberta).location ?? ''}
                      placeholder="Sao Paulo, State of Sao Paulo, Brazil"
                      onChange={(e) => editFeed({ location: e.target.value })}
                      onBlur={persistFeedNow}
                    />
                  </Field>
                  <Field label="Nome da cidade (como aparece no título)">
                    <input
                      className={styles.input}
                      value={feedOf(aberta).cidade ?? ''}
                      placeholder="São Paulo — com acento; vazio usa a geolocalização"
                      onChange={(e) => editFeed({ cidade: e.target.value })}
                      onBlur={persistFeedNow}
                    />
                  </Field>
                  <Field label="Período">
                    <select
                      className={styles.input}
                      value={feedOf(aberta).periodo ?? ''}
                      onChange={(e) => {
                        editFeed({ periodo: e.target.value });
                      }}
                      onBlur={persistFeedNow}
                    >
                      <option value="">Qualquer data</option>
                      <option value="today">Hoje</option>
                      <option value="weekend">Fim de semana</option>
                      <option value="next_week">Próxima semana</option>
                    </select>
                  </Field>
                  <p className={styles.blocoHint}>
                    Processamento "Template determinístico" = UMA página-agenda PERENE por cidade
                    (sem IA, custo zero): a primeira rodada cria a página e as seguintes ATUALIZAM a
                    mesma URL — endereço e data de publicação nunca mudam. "1×1" = uma resenha por
                    evento (IA, exige descrição na fonte).
                  </p>
                </>
              )}
              {feedOf(aberta).kind === 'serp_finance' && (
                <>
                  <Field label="Papel (vazio = painel geral)">
                    <input
                      className={styles.input}
                      value={feedOf(aberta).ticker ?? ''}
                      placeholder="ex.: ABEV3:BVMF · PETR4:BVMF"
                      onChange={(e) => editFeed({ ticker: e.target.value })}
                      onBlur={persistFeedNow}
                    />
                  </Field>
                  <p className={styles.blocoHint}>
                    Com papel: as notícias DAQUELE ativo viram a fonte da matéria. Sem papel: painel
                    dos mercados (índices, moedas, cripto) + notícias do dia — o resumo do pregão.
                    Números de IBOV/dólar grátis vêm da fábrica; a busca SERP entra 1× por rodada.
                  </p>
                </>
              )}
              {feedOf(aberta).kind === 'rss' && (
                <Field label="URL do feed (RSS)">
                  <input
                    className={styles.input}
                    value={feedOf(aberta).url ?? ''}
                    placeholder="https://veiculo.com.br/feed"
                    onChange={(e) => editFeed({ url: e.target.value })}
                    onBlur={persistFeedNow}
                  />
                </Field>
              )}
              {feedOf(aberta).kind === 'api' && (
                <Field label="Fonte de dados">
                  <div className={styles.ro}>{feedOf(aberta).fonte}</div>
                </Field>
              )}
              {(feedOf(aberta).kind === 'serp_trends' || feedOf(aberta).kind === 'mos') && (
                <Field label="Origem">
                  <div className={styles.ro}>
                    {feedOf(aberta).kind === 'mos'
                      ? 'push do NRCS — sem URL'
                      : 'SERP Trends — automático'}
                  </div>
                </Field>
              )}
              {feedOf(aberta).kind === 'fabrica' && (
                <>
                  <Field label="Fonte de dados">
                    <div className={styles.ro}>Platform Core · custo LLM zero</div>
                  </Field>
                  {feedOf(aberta).fabrica === 'loterias' && (
                    <Field label="Jogos">
                      <ChipToggle
                        options={(factoriesStatus.data?.jogos_validos ?? DEFAULT_JOGOS).map(
                          (k) => ({
                            value: k,
                            label: GAME_LABEL[k] ?? k,
                          }),
                        )}
                        selected={feedOf(aberta).jogos ?? []}
                        onChange={(next) => {
                          editFeed({ jogos: next });
                          if (aberta) void persistFeed({ ...feedOf(aberta), jogos: next });
                        }}
                      />
                    </Field>
                  )}
                  {feedOf(aberta).fabrica === 'clima' && (
                    <Field label="Capitais">
                      <ChipToggle
                        options={CAPITAIS_CHIPS}
                        selected={feedOf(aberta).cidades ?? []}
                        onChange={(next) => {
                          editFeed({ cidades: next });
                          if (aberta) void persistFeed({ ...feedOf(aberta), cidades: next });
                        }}
                      />
                    </Field>
                  )}
                  {feedOf(aberta).fabrica === 'mercado' && (
                    <Field label="Tickers B3 (+ ^BVSP)">
                      <input
                        className={styles.input}
                        value={(feedOf(aberta).simbolos ?? []).join(', ')}
                        placeholder="^BVSP, PETR4, VALE3"
                        onChange={(e) =>
                          editFeed({
                            simbolos: e.target.value
                              .split(',')
                              .map((x) => x.trim())
                              .filter(Boolean),
                          })
                        }
                        onBlur={persistFeedNow}
                      />
                      <span className={styles.help}>
                        Universo completo da B3 chega em breve; por ora, liste os tickers.
                      </span>
                    </Field>
                  )}
                </>
              )}
              <div className={styles.row}>
                {feedOf(aberta).kind !== 'fabrica' && (
                  <Field label="Itens/ciclo">
                    <input
                      type="number"
                      min={1}
                      max={50}
                      className={styles.input}
                      value={feedOf(aberta).itens ?? 1}
                      onChange={(e) => editFeed({ itens: Number(e.target.value) || 1 })}
                      onBlur={persistFeedNow}
                    />
                  </Field>
                )}
                <Field
                  label={
                    feedOf(aberta).kind === 'fabrica' ? 'Janela (BRT)' : 'Intervalo ou janela (BRT)'
                  }
                >
                  <input
                    className={styles.input}
                    value={feedOf(aberta).intervalo}
                    onChange={(e) => editFeed({ intervalo: e.target.value })}
                    onBlur={persistFeedNow}
                    placeholder='ex.: "1h" · "8h" · "6-9h b3" · "10-12h sex" · "8-10h feriado"'
                    title={
                      'Frequência ("1h", "8h", "diário") OU janela de horário BRT (roda 1x dentro do horário).\n' +
                      'DIAS: "úteis" = seg-sex E SEM FERIADO · "b3" = dias em que a bolsa negocia ' +
                      '(pula também segunda de Carnaval, 24/12 e 31/12) · dias explícitos "sex", ' +
                      '"qua,dom" · "feriado" = SÓ em feriado · "corridos" = todo dia, feriado incluso.\n' +
                      'Ex.: mercado pré-abertura = "6-9h b3"; agenda cultural = "10-12h sex"; ' +
                      'horóscopo = "diário".'
                    }
                  />
                  {janelaErro && (
                    <p className={styles.pubErr} role="alert">{janelaErro}</p>
                  )}
                  <span className={styles.hint}>
                    <strong>úteis</strong> pula feriado · <strong>b3</strong> usa o calendário da
                    bolsa · <strong>feriado</strong> roda só em feriado · <strong>corridos</strong>{' '}
                    ignora feriado —{' '}
                    <Link to="/config/publicacao">ver calendário e cadastrar feriado local</Link>
                  </span>
                </Field>
                {(feedOf(aberta).kind === 'google_news' || feedOf(aberta).kind === 'rss') && (
                  <Field label="Frescor máximo da fonte">
                    <input
                      className={styles.input}
                      value={feedOf(aberta).janela_frescor ?? ''}
                      onChange={(e) => editFeed({ janela_frescor: e.target.value })}
                      onBlur={persistFeedNow}
                      placeholder='vazio = sem filtro · ex.: "24h" · "48h" · "7d"'
                      title={
                        'Descarta itens do feed mais velhos que isto ANTES de gerar. Item sem data legível também é descartado. Vazio = sem filtro (comportamento antigo). Recomendado: "24h" para editorias de notícia do dia.'
                      }
                    />
                  </Field>
                )}
              </div>
            </div>

            <span className={styles.bigArrow}>›</span>

            {/* BLOCO 2 — PROCESSAMENTO */}
            <div className={styles.bloco}>
              <div className={styles.blocoHead}>
                <span className={styles.blocoNum}>2</span> Processamento
              </div>
              <Field label="Modo">
                <select
                  className={styles.input}
                  value={aberta.proc.mode}
                  onChange={(ev) =>
                    patch(aberta.slug, {
                      proc: { ...aberta.proc, mode: ev.target.value as ProcMode },
                    })
                  }
                >
                  {(Object.keys(PROC_LABEL) as ProcMode[]).map((k) => (
                    <option key={k} value={k}>
                      {PROC_LABEL[k]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Modelo LLM (BYOK)">
                <select
                  className={styles.input}
                  value={aberta.proc.modelo ?? ''}
                  onChange={(e) =>
                    patch(aberta.slug, { proc: { ...aberta.proc, modelo: e.target.value } })
                  }
                >
                  {[...new Set([aberta.proc.modelo ?? '(sem IA)', ...LLM_MODELS])].map((m) => (
                    <option key={m} value={m === '(sem IA)' ? '' : m}>
                      {m}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Prompt">
                <div className={styles.ro}>prompt travado (edição na UI em breve)</div>
              </Field>
              <label className={styles.check}>
                <input
                  type="checkbox"
                  checked={aberta.proc.dedup}
                  onChange={(ev) =>
                    patch(aberta.slug, { proc: { ...aberta.proc, dedup: ev.target.checked } })
                  }
                />
                <span>Dedup de itens (fingerprint)</span>
              </label>
            </div>

            <span className={styles.bigArrow}>›</span>

            {/* BLOCO 3 — PUBLICAÇÃO */}
            <div className={styles.bloco}>
              <div className={styles.blocoHead}>
                <span className={styles.blocoNum}>3</span> Publicação
              </div>
              <Field label="Sites WP">
                <div className={styles.sites}>
                  {siteOptions.map((s) => (
                    <label key={s.slug} className={styles.siteChk}>
                      <input
                        type="checkbox"
                        checked={aberta.pub.sites.includes(s.slug)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...aberta.pub.sites, s.slug]
                            : aberta.pub.sites.filter((x) => x !== s.slug);
                          patch(aberta.slug, { pub: { ...aberta.pub, sites: next } });
                        }}
                      />
                      <span>{s.name}</span>
                    </label>
                  ))}
                  {siteOptions.length === 0 && (
                    <span className={styles.muted}>nenhum site cadastrado</span>
                  )}
                </div>
                {orphanSites.length > 0 && (
                  <p className={styles.muted}>
                    site(s) não cadastrados nesta instância:{' '}
                    {orphanSites.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={styles.orphanChip}
                        title="remover referência órfã"
                        onClick={() =>
                          patch(aberta.slug, {
                            pub: {
                              ...aberta.pub,
                              sites: aberta.pub.sites.filter((x) => x !== s),
                            },
                          })
                        }
                      >
                        {s} ×
                      </button>
                    ))}
                  </p>
                )}
              </Field>
              <Field label="Categoria">
                {firstSite && catsQuery.data && catsQuery.data.length > 0 ? (
                  <select
                    className={styles.input}
                    value={aberta.pub.categoria_id ?? ''}
                    onChange={(e) => {
                      const id = Number(e.target.value) || undefined;
                      const cat = (catsQuery.data ?? []).find((c) => c.id === id);
                      patch(aberta.slug, {
                        pub: {
                          ...aberta.pub,
                          categoria_id: id,
                          categoria: cat ? cat.name : aberta.pub.categoria,
                        },
                      });
                    }}
                  >
                    <option value="">{aberta.pub.categoria || '— escolher —'}</option>
                    {catsQuery.data.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className={styles.ro}>
                    {aberta.pub.categoria || '—'}
                    {firstSite && catsQuery.isLoading ? ' · carregando…' : ''}
                    {firstSite && catsQuery.isError ? ' · categorias indisponíveis' : ''}
                  </div>
                )}
              </Field>
              <div className={styles.row}>
                <Field label="Status">
                  <select
                    className={styles.input}
                    value={aberta.pub.status}
                    onChange={(ev) =>
                      patch(aberta.slug, {
                        pub: { ...aberta.pub, status: ev.target.value as 'publish' | 'draft' },
                      })
                    }
                  >
                    <option value="publish">Direto (publish)</option>
                    <option value="draft">Revisão (draft)</option>
                  </select>
                </Field>
                <Field label="Por hora">
                  <div className={styles.ro}>{aberta.pub.porHora}</div>
                </Field>
              </div>
              <Field label="Autor / AXIOMA">
                <div className={styles.ro}>{aberta.pub.autor} · story automática</div>
              </Field>
            </div>
          </div>

          <div className={styles.detFoot}>
            <span className={styles.proto}>
              {mut.isPending ? 'A gravar…' : 'Conectado ao backend · alterações persistem (E1)'}
            </span>
            <div className={styles.footBtns}>
              {isFabrica ? (
                <Button
                  size="sm"
                  onClick={() => {
                    setRunMsg(null);
                    runFabrica.mutate({ slug: aberta.slug, client: aberta.client_id ?? 'casa' });
                  }}
                  loading={runFabrica.isPending}
                  disabled={aberta.pub.sites.length === 0}
                  title={
                    aberta.pub.sites.length === 0
                      ? 'Escolha um site de destino primeiro'
                      : undefined
                  }
                >
                  Rodar agora ({aberta.pub.status === 'publish' ? 'publica' : 'rascunho'})
                </Button>
              ) : (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => ingestMut.mutate(aberta.slug)}
                    loading={ingestMut.isPending}
                  >
                    Rodar feed (prévia)
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => runMut.mutate(aberta.slug)}
                    loading={runMut.isPending}
                  >
                    Gerar rascunho (prévia)
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setPubErr(null);
                      publishMut.mutate({ slug: aberta.slug, live: false });
                    }}
                    loading={publishMut.isPending && publishMut.variables?.live === false}
                  >
                    Salvar rascunho no WP
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      if (
                        !window.confirm(
                          'Publicar AO VIVO no WordPress? A matéria vai ao ar imediatamente.',
                        )
                      )
                        return;
                      setPubErr(null);
                      publishMut.mutate({ slug: aberta.slug, live: true });
                    }}
                    loading={publishMut.isPending && publishMut.variables?.live === true}
                  >
                    Publicar ao vivo
                  </Button>
                </>
              )}
            </div>
          </div>

          {isFabrica && (
            <div className={styles.fabricaPanel}>
              <div className={styles.previewCol}>
                <span className={styles.fieldLabelSm}>Prévia da capa (marca do destino)</span>
                {aberta.pub.sites.length === 0 ? (
                  <div className={styles.previewEmpty}>
                    Escolha um site para ver a capa com a marca dele.
                  </div>
                ) : preview.isLoading ? (
                  <div className={styles.previewEmpty}>A gerar prévia…</div>
                ) : preview.isError ? (
                  <div className={styles.previewEmpty}>Prévia indisponível.</div>
                ) : preview.data ? (
                  <img
                    className={styles.previewImg}
                    src={`data:image/png;base64,${preview.data.png_base64}`}
                    alt="Prévia da capa gerada para esta fábrica"
                  />
                ) : null}
                {runMsg && <p className={styles.runMsg}>{runMsg}</p>}
              </div>
              <div className={styles.ultimasCol}>
                <span className={styles.fieldLabelSm}>Últimas desta editoria</span>
                {ultimas.isLoading ? (
                  <div className={styles.previewEmpty}>Carregando…</div>
                ) : (ultimas.data?.itens.length ?? 0) === 0 ? (
                  <div className={styles.previewEmpty}>
                    Nada publicado ainda. Clique em “Rodar agora”.
                  </div>
                ) : (
                  <ul className={styles.ultimasList}>
                    {(ultimas.data?.itens ?? []).map((it, i) => (
                      <li key={i} className={styles.ultimaItem}>
                        <Badge tone={it.estado === 'publicada' ? 'ok' : 'neutral'} size="xs">
                          {it.estado === 'publicada' ? 'no ar' : 'rascunho'}
                        </Badge>
                        {it.wp_link ? (
                          <a
                            href={it.wp_link}
                            target="_blank"
                            rel="noreferrer"
                            className={styles.ultimaLink}
                          >
                            {it.tema}
                          </a>
                        ) : (
                          <span className={styles.ultimaTema}>{it.tema}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {pubErr && <p className={styles.pubErr}>{pubErr}</p>}

          {ingest && ingest.slug === aberta.slug && (
            <div className={styles.ingest}>
              <div className={styles.ingestHead}>
                <strong>Feed → dedup</strong> · {ingest.total} itens ·{' '}
                <span className={styles.ingestNovos}>{ingest.novos} novos</span> ·{' '}
                <span className={styles.muted}>{ingest.duplicados} já vistos</span>
                {ingest.duplicados > 0 && ingest.novos === 0 && (
                  <span className={styles.ingestDedup}> dedup </span>
                )}
                <button className={styles.runClose} onClick={() => setIngest(null)}>
                  ×
                </button>
              </div>
              <ul className={styles.ingestList}>
                {ingest.itens.map((it, i) => (
                  <li key={i} className={styles.ingestItem}>
                    <Badge tone={it.novo ? 'info' : 'neutral'} size="xs">
                      {it.novo ? 'novo' : 'visto'}
                    </Badge>
                    <span className={styles.ingestTitle}>{it.titulo}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {draft && draft.slug === aberta.slug && (
            <div className={styles.draft}>
              <div className={styles.ingestHead}>
                <strong>Rascunho gerado</strong> ·{' '}
                <span className={styles.muted}>
                  {draft.mode} · {draft.modelo}
                </span>
                {typeof draft.custo_usd === 'number' && (
                  <span className={styles.muted}> · US$ {draft.custo_usd.toFixed(4)}</span>
                )}
                {draft.texto_fonte && (
                  <Badge tone={draft.texto_fonte === 'completo' ? 'ok' : 'neutral'} size="xs">
                    {draft.texto_fonte === 'completo' ? 'texto completo' : 'só resumo'}
                  </Badge>
                )}
                <Badge
                  tone={
                    draft.wp_link ? (draft.status_destino === 'publish' ? 'ok' : 'info') : 'warn'
                  }
                  size="xs"
                >
                  {draft.wp_link
                    ? draft.status_destino === 'publish'
                      ? 'PUBLICADO ao vivo'
                      : 'rascunho no WP'
                    : 'prévia · não publica'}
                </Badge>
                <button className={styles.runClose} onClick={() => setDraft(null)}>
                  ×
                </button>
              </div>
              {draft.gerado ? (
                <>
                  <h3 className={styles.draftTitle}>{draft.titulo}</h3>
                  <div
                    className={styles.draftBody}
                    dangerouslySetInnerHTML={{ __html: sanitizarHtml(draft.html) }}
                  />
                  {draft.fontes && draft.fontes.length > 0 && (
                    <p className={styles.draftFontes}>
                      Fonte: {draft.fontes.map((f) => f.titulo).join(' · ')}
                    </p>
                  )}
                  {draft.wp_link && (
                    <p className={styles.draftFontes}>
                      {' '}
                      {draft.status_destino === 'publish'
                        ? 'Publicado ao vivo'
                        : 'Rascunho salvo (draft)'}{' '}
                      ·{' '}
                      <a
                        href={draft.wp_link}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.wpLink}
                      >
                        ver no WordPress →
                      </a>
                    </p>
                  )}
                </>
              ) : (
                <p className={styles.muted}>{draft.motivo ?? 'Nada gerado.'}</p>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

export type { Editoria };
